import { CorporateCrest, TwistedCrest } from './Crest';
import { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const links = [
  { id: 'pillars', label: 'Breakthroughs', testid: 'nav-link-breakthroughs' },
  { id: 'family', label: 'The Family', testid: 'nav-link-family' },
  { id: 'town', label: 'St. Bellmore', testid: 'nav-link-town' },
];

export const Nav = () => {
  const [clicks, setClicks] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const timer = useRef(null);

  const handleCrestClick = () => {
    const n = clicks + 1;
    setClicks(n);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setClicks(0), 1800);
    if (n >= 4) {
      setClicks(0);
      clearTimeout(timer.current);
      setRevealed(true);
    }
  };

  return (
    <>
    <header className="fixed top-0 inset-x-0 z-50 bg-[#190A0B]/85 backdrop-blur-md border-b border-[#47201F]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" onClick={handleCrestClick} className="flex items-center gap-3 group cursor-pointer">
          <motion.span
            key={clicks}
            animate={clicks ? { x: [0, -2.5, 2.5, -1, 0], rotate: [0, -2, 2, -1, 0] } : {}}
            transition={{ duration: 0.35 }}
            className="block"
          >
            {revealed ? (
              <TwistedCrest className="w-8 h-10" />
            ) : (
              <CorporateCrest className="w-8 h-10 transition-transform duration-500 group-hover:scale-105" cracks={Math.min(clicks, 3)} />
            )}
          </motion.span>
          <span className="leading-none">
            <span className="block font-serif font-semibold text-lg tracking-wide text-[#F2E5CF]">THE LEÓN FOUNDATION</span>
            <span className="block font-mono text-[10px] tracking-[0.3em] text-[#94756A] mt-1">EST. 1842 · ST. BELLMORE</span>
            <span
              data-testid="crest-ritual-counter"
              className={`flex gap-1.5 mt-1.5 transition-opacity duration-300 ${clicks > 0 && !revealed ? 'opacity-100' : 'opacity-0'}`}
              aria-hidden="true"
            >
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={i}
                  className={`w-1.5 h-1.5 rotate-45 border transition-colors duration-200 ${
                    i < clicks ? 'bg-[#DC2626] border-[#DC2626]' : 'border-[#94756A]/50'
                  }`}
                />
              ))}
            </span>
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={l.testid}
              className="font-display text-sm font-medium text-[#B99C8B] hover:text-[#F2E5CF] transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#town"
            data-testid="nav-cta-report"
            className="font-display text-sm font-semibold tracking-wide text-[#C9A227] border border-[#C9A227]/60 px-5 py-2.5 hover:bg-[#C9A227] hover:text-[#190A0B] transition-colors duration-300"
          >
            Annual Report
          </a>
        </nav>
      </div>
    </header>

    <AnimatePresence>
        {revealed && (
          <motion.div
            data-testid="crest-ritual-reveal"
            className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#08070B]/95 scanlines cursor-pointer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setRevealed(false)}
          >
            <motion.div
              initial={{ scale: 0.55, rotate: -10, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              exit={{ scale: 1.15, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 110, damping: 12 }}
              className="sanctum-glow"
            >
              <TwistedCrest className="w-40 h-48 sm:w-52 sm:h-60" />
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="mt-10 font-mono text-[10px] sm:text-xs tracking-[0.5em] uppercase text-[#DC2626]"
            >
              Per Sanguinem Immortalis
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mt-3 font-serif italic text-sm text-gray-500"
            >
              The crest remembers what it truly guards.
            </motion.p>
            <motion.a
              href="/archive"
              data-testid="ritual-archive-link"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.15 }}
              className="mt-8 font-mono text-[9px] tracking-[0.45em] uppercase text-[#D4AF37]/70 hover:text-[#D4AF37] underline underline-offset-4 transition-colors"
            >
              the archive opens
            </motion.a>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4 }}
              className="mt-6 font-mono text-[8px] tracking-[0.35em] uppercase text-gray-600"
            >
              click anywhere to close the vision
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
